import { handleActions } from 'redux-actions';
import LinkedList from '../helpers/LinkedList';

const defaultState = {
  error: { type: null, message: null },

  autoSearch: true,
  searching: false,
  searchType: null, // 'IMAGES', 'SINGLE_EVAL', 'BATCH_EVAL'

  imageSearchResult: {
    id: null,
    batch: null,
    ext: null,
    height: null,
    width: null,
    insts: [],
    jumboid: null,
    source: null,
    timestamp: null,
    url: null
  },
  imagePolygonInstances: [],
  imageRLEInstances: [],

  /*
   *  Current image node stores the linked-list's node data type when
   *  user is viewing at single image evaluation
   */
  currentImageNode: null,
  imagesSearchResult: {
    images: new LinkedList([]),
    totalPage: 0,
    currentPage: 0,
    total: 0
  },
  selectedImages: new Set()
};

const mapImageFunction = ({
  batch_name: batchName,
  batch_sn: batchSN,
  camera_id: cameraID,
  create_time: createTime,
  file_ext: ext,
  frame_time: frameTime,
  height,
  job_type: jobType,
  ori_key: oriKey,
  path,
  signed_url: url,
  sn,
  source_name: sourceName,
  source_sn: sourceSN,
  width
}) => ({
  batch: { name: batchName, sn: batchSN },
  source: { name: sourceName, sn: sourceSN },
  cameraID,
  createTime,
  ext,
  frameTime,
  width,
  height,
  jobType,
  oriKey,
  path,
  url,
  sn
});

const handleUpdateImageSearchResult = (state, {
  payload: {
    sn,
    batch_name: batchName,
    batch_sn: batchSN,
    source_name: sourceName,
    source_sn: sourceSN,
    camera_id: cameraID,
    create_time: createTime,
    file_ext: ext,
    frame_time: frameTime,
    job_type: jobType,
    ori_key: oriKey,
    path,
    signed_url: url,
    width,
    height
  }
}) => ({
  ...state,
  imageSearchResult: {
    sn,
    batch: { name: batchName, sn: batchSN },
    source: { name: sourceName, sn: sourceSN },
    cameraID,
    createTime,
    ext,
    frameTime,
    jobType,
    oriKey,
    path,
    url,
    width,
    height
  }
});

const handleUpdateImageInstances = (state, { payload }) => {
  const { instances } = payload;
  const polygons = [];
  // const bboxes = [];
  const rle = [];

  instances.forEach((instance) => {
    const { encoding } = instance;

    if (encoding === 'polygon' || encoding === 'bbox') {
      polygons.push(instance);
    } else if (encoding === 'rle') {
      rle.push(instance);
    // } else if (encoding === 'bbox') {
    //   bboxes.push(instance);
    } else {
      console.warn(`Unsupported encoding \`${encoding}\``);
    }
  });
  return ({
    ...state,
    imagePolygonInstances: polygons.map(({
      sn,
      area,
      box,
      category_name: categoryName,
      category_sn: categorySN,
      encoding,
      image_sn: imageSN,
      is_group: isGroup,
      seg,
      umbo_category_name: umboCategoryName,
      umbo_category_sn: umboCategorySN
    }) => ({
      sn,
      area,
      box,
      encoding,
      imageSN,
      isGroup,
      seg,
      category: { name: categoryName, sn: categorySN },
      umboCategory: { name: umboCategoryName, sn: umboCategorySN }
    }))
  });
};
const handleUpdateImagesSearchResult = (state, {
  payload: {
    images,
    page,
    pages,
    total
  }
}) => ({
  ...state,
  imagesSearchResult: {
    images: new LinkedList(images.map(mapImageFunction)),
    total,
    totalPage: pages,
    currentPage: page
  }
});

const handleUpdateImagesNextPage = (state, { payload: { images, page, pages } }) => ({
  ...state,
  imagesSearchResult: {
    images: state.imagesSearchResult.images.concat(
      images.map(mapImageFunction)
    ),
    totalPage: pages,
    currentPage: page,
    total: state.imagesSearchResult.total
  }
});
const handleUpdateSelectedModel = (state, { payload }) => ({
  ...state,
  selectedModel: payload
});
const handleUpdateCurrentImageNode = (state, { payload: imageSN }) => ({
  ...state,
  currentImageNode: state.imagesSearchResult.images.nodes()
    .find(({ value }) => value.id === Number(imageSN))
});
const handleClearCurrentImageNode = state => ({
  ...state,
  currentImageNode: null
});

/* Searching Image */
const handleSearchActionConstructor = searchType => state => ({
  ...state,
  searching: true,
  searchType,
  error: { type: null, message: null }
});
const handleSearchResultConstructor = message => state => ({
  ...state,
  searching: false,
  error: { type: 'SEARCH_IMAGE_ERROR', message }
});
const handleSearchComplete = state => ({
  ...state,
  searching: false,
  searchType: null,
  error: { type: null, message: null }
});

const handleSelectImage = (state, { payload }) => {
  const { selectedImages } = state;
  selectedImages.add(payload);

  return {
    ...state,
    selectedImages: new Set(Array.from(selectedImages))
  };
};
const handleUpdateSelectedImages = (state, { payload }) => ({
  ...state,
  selectedImages: new Set(payload)
});
const handleSelectAllImages = state => ({
  ...state,
  selectedImages: state.imagesSearchResult.images.reduce(
    (set, { id }) => set.add(id),
    new Set()
  )
});
const handleDeselectImage = (state, { payload }) => {
  const { selectedImages } = state;
  selectedImages.delete(payload);

  return {
    ...state,
    selectedImages: new Set(Array.from(selectedImages))
  };
};
const handleClearImageSelection = state => ({
  ...state,
  selectedImages: new Set()
});

const handleToggleAutoSearch = state => ({
  ...state,
  autoSearch: !state.autoSearch
});

const handleClearImage = state => ({
  ...state,
  imageSearchResult: {
    id: null,
    batch: null,
    ext: null,
    height: null,
    width: null,
    insts: [],
    jumboid: null,
    source: null,
    timestamp: null,
    url: null
  },
  imagePolygonInstances: [],
  imageRLEInstances: []
});

const SearchFieldReducer = handleActions(
  {
    UPDATE_IMAGE_SEARCH_RESULT: handleUpdateImageSearchResult,
    UPDATE_IMAGE_INSTANCES: handleUpdateImageInstances,
    UPDATE_IMAGES_SEARCH_RESULT: handleUpdateImagesSearchResult,
    UPDATE_IMAGES_NEXT_PAGE: handleUpdateImagesNextPage,
    UPDATE_SELECTED_MODEL: handleUpdateSelectedModel,
    UPDATE_CURRENT_IMAGE_NODE: handleUpdateCurrentImageNode,
    CLEAR_CURRENT_IMAGE_NODE: handleClearCurrentImageNode,

    CLEAR_IMAGE: handleClearImage,
    SEARCH_IMAGE: handleSearchActionConstructor('SINGLE_EVAL'),
    SEARCH_IMAGES: handleSearchActionConstructor('IMAGES'),
    SEARCH_IMAGES_NEXT_PAGE: handleSearchActionConstructor('IMAGES'),
    SEARCH_COMPLETE: handleSearchComplete,
    SEARCH_CANCEL: handleSearchResultConstructor('400 Request Cancelled'),
    SEARCH_TIMEOUT: handleSearchResultConstructor('408 Request Timeout'),
    SEARCH_FAILURE: handleSearchActionConstructor('404 Not Found'),

    SELECT_IMAGE: handleSelectImage,
    SELECT_ALL_IMAGES: handleSelectAllImages,
    UPDATE_SELECTED_IMAGES: handleUpdateSelectedImages,
    DESELECT_IMAGE: handleDeselectImage,
    CLEAR_IMAGE_SELECTION: handleClearImageSelection,

    TOGGLE_AUTO_SEARCH: handleToggleAutoSearch
  },
  defaultState
);

export default SearchFieldReducer;



// WEBPACK FOOTER //
// ./src/reducers/SearchFieldReducer.js